import type {
  AnyEquation,
  Equation,
  EquationDocument,
  EvaluationResult,
} from "../equation";
import type { Dimension } from "@src/math/types";
import {
  isEvaluationAbort,
  type EquationEvaluator,
} from "./evaluator-contract";

export interface EquationEvaluationRequest<D extends Dimension> {
  readonly signal: AbortSignal;
  evaluate(document: EquationDocument<D>): Promise<EvaluationResult<D>>;
  isCurrent(): boolean;
  cancel(): void;
}

export interface EquationEvaluationCoordinator {
  begin(dimension: 2): EquationEvaluationRequest<2>;
  begin(dimension: 3): EquationEvaluationRequest<3>;
  schedule(
    equation: Equation<2>,
    coalesce: boolean,
    onResult: (result: EvaluationResult<2>) => void,
  ): void;
  schedule(
    equation: Equation<3>,
    coalesce: boolean,
    onResult: (result: EvaluationResult<3>) => void,
  ): void;
  cancelAll(): void;
}

export const EQUATION_EVALUATION_DEBOUNCE_MS = 100;

export function createEquationEvaluationCoordinator(
  evaluateEquation: EquationEvaluator,
  onError: (error: unknown) => void = reportEvaluationError,
): EquationEvaluationCoordinator {
  const controllers = new Map<Dimension, AbortController>();
  const pendingTimers = new Map<Dimension, ReturnType<typeof setTimeout>>();

  function createRequest<D extends Dimension>(
    dimension: D,
    evaluate: (
      document: EquationDocument<D>,
      options: { readonly signal: AbortSignal },
    ) => Promise<EvaluationResult<D>>,
  ): EquationEvaluationRequest<D> {
    controllers.get(dimension)?.abort();
    const controller = new AbortController();
    controllers.set(dimension, controller);
    const isCurrent = (): boolean =>
      controllers.get(dimension) === controller && !controller.signal.aborted;
    return {
      signal: controller.signal,
      evaluate: (document) => evaluate(document, { signal: controller.signal }),
      isCurrent,
      cancel() {
        if (!isCurrent()) return;
        controller.abort();
        controllers.delete(dimension);
      },
    };
  }

  function begin(dimension: 2): EquationEvaluationRequest<2>;
  function begin(dimension: 3): EquationEvaluationRequest<3>;
  function begin(
    dimension: Dimension,
  ): EquationEvaluationRequest<2> | EquationEvaluationRequest<3> {
    return dimension === 2
      ? createRequest(2, (document, options) =>
          evaluateEquation(document, options),
        )
      : createRequest(3, (document, options) =>
          evaluateEquation(document, options),
        );
  }

  function schedule(
    pendingEquation: Equation<2>,
    coalesce: boolean,
    onResult: (result: EvaluationResult<2>) => void,
  ): void;
  function schedule(
    pendingEquation: Equation<3>,
    coalesce: boolean,
    onResult: (result: EvaluationResult<3>) => void,
  ): void;
  function schedule(
    pendingEquation: AnyEquation,
    coalesce: boolean,
    onResult:
      | ((result: EvaluationResult<2>) => void)
      | ((result: EvaluationResult<3>) => void),
  ): void {
    const dimension = pendingEquation.dimension;
    const pendingTimer = pendingTimers.get(dimension);
    if (pendingTimer !== undefined) {
      clearTimeout(pendingTimer);
      pendingTimers.delete(dimension);
    }
    if (pendingEquation.dimension === 2)
      scheduleKnownEquation(
        pendingEquation,
        coalesce,
        pendingTimers,
        begin(2),
        onResult as (result: EvaluationResult<2>) => void,
        onError,
      );
    else
      scheduleKnownEquation(
        pendingEquation,
        coalesce,
        pendingTimers,
        begin(3),
        onResult as (result: EvaluationResult<3>) => void,
        onError,
      );
  }

  function scheduleKnownEquation<D extends Dimension>(
    pendingEquation: Equation<D>,
    coalesce: boolean,
    timers: Map<Dimension, ReturnType<typeof setTimeout>>,
    request: EquationEvaluationRequest<D>,
    onResult: (result: EvaluationResult<D>) => void,
    reportError: (error: unknown) => void,
  ): void {
    const dimension = pendingEquation.dimension;
    const evaluate = (): void => {
      if (!request.isCurrent()) return;
      void request
        .evaluate(pendingEquation)
        .catch((error: unknown): EvaluationResult<D> => {
          if (isEvaluationAbort(error)) throw error;
          return {
            kind: "invalid",
            diagnostics: [{ code: "evaluation-unavailable" }],
          };
        })
        .then((result) => {
          if (request.isCurrent()) onResult(result);
        })
        .catch((error: unknown) => {
          if (!isEvaluationAbort(error)) reportError(error);
        });
    };
    if (!coalesce) {
      evaluate();
      return;
    }
    const timer = setTimeout(() => {
      if (timers.get(dimension) === timer) timers.delete(dimension);
      evaluate();
    }, EQUATION_EVALUATION_DEBOUNCE_MS);
    timers.set(dimension, timer);
  }

  return {
    begin,
    schedule,
    cancelAll() {
      controllers.forEach((controller) => {
        controller.abort();
      });
      controllers.clear();
      pendingTimers.forEach((timer) => {
        clearTimeout(timer);
      });
      pendingTimers.clear();
    },
  };
}

function reportEvaluationError(error: unknown): void {
  console.error("Equation evaluation failed", error);
}
